import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { ArrowLeft, AlertTriangle, CheckCircle, Upload, FileText, Trash2, Ship, Shield, Building2, Calendar, Lock, Edit3, Package } from 'lucide-react'
import {
  projectsApi,
  AssessmentResponse,
  AssetInventoryRow,
  EvidenceFile,
  ParseAssessmentResultLegacy,
} from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { ImportAssessmentModal } from '@/components/modals/ImportAssessmentModal'
import { ImportLegacyRaModal } from '@/components/modals/ImportLegacyRaModal'
import { ImportSystemAssetInventoryModal } from '@/components/modals/ImportSystemAssetInventoryModal'
import { FullEditAssessmentModal } from '@/components/modals/FullEditAssessmentModal'
import { EvidenceUploader } from '@/components/risk/EvidenceUploader'
import { EditablePill } from '@/components/risk/EditablePill'
import { EditableText } from '@/components/risk/EditableText'
import { LegacyRaView } from '@/components/risk/LegacyRaView'
import { WordRaSystemView } from '@/components/risk/WordRaSystemView'
import { AssetInventoryTab } from '@/components/AssetInventoryTab'
import { EvidenceSection, EvidenceSectionHandle } from '@/components/risk/EvidenceSection'
import { useDocumentDropRouter, DropDecision } from '@/hooks/useDocumentDropRouter'
import { isEvidenceTypeAllowed, EVIDENCE_ALLOWED_LABEL } from '@/lib/evidenceFileTypes'
import { useAuth } from '@/contexts/AuthContext'
import { canUpload as canUploadFiles, canManage } from '@/lib/roles'
import { validateUploadSize, MAX_UPLOAD_LABEL } from '@/lib/uploadLimits'
import { DirtyFormProvider } from '@/contexts/DirtyFormContext'
import { DirtyChangesBar } from '@/components/DirtyChangesBar'
import { useTranslation } from 'react-i18next'

const CBS_QUESTIONS = [
  { code: 'q1', num: 1, category: 'Physical (HW)', text: 'Is there a digital display or input device?' },
  { code: 'q2', num: 2, category: 'Physical (HW)', text: 'Is there a programmable electronic device (CPU / MCU / PLC)?' },
  { code: 'q3', num: 3, category: 'Physical (HW)', text: 'Is there a connection port for maintenance or external interface?' },
  { code: 'q4', num: 4, category: 'Function', text: 'Does it have firmware / software versions or configurable parameters?' },
  { code: 'q5', num: 5, category: 'Function', text: 'Can it process data digitally or communicate via protocol?' },
  { code: 'q6', num: 6, category: 'Function', text: 'Can it display or store diagnostic information or event history?' },
]

const ATTACHMENT_SLOTS = [
  { key: 'F1', label: 'External / Internal photos', required: true },
  { key: 'F2', label: 'Circuit / Connection / Network diagram', required: true },
  { key: 'F3', label: 'User Manual / Datasheet / Catalog', required: true },
  { key: 'F4', label: 'Type Approval / Exemption Letter', required: false },
]

// synthesize filenames for clipboard-pasted images (pasted Blobs have no
// name). strips filesystem-illegal chars, collapses whitespace and
// repeats, trims, caps length so the composed filename stays well under
// any reasonable filesystem limit
const sanitizeFilename = (s: string): string =>
  s.replace(/[/\\?%*:|"<>]/g, '_')
   .replace(/\s+/g, '_')
   .replace(/_+/g, '_')
   .replace(/^_|_$/g, '')
   .slice(0, 80)

export const SystemDetailPage: React.FC = () => {
  const { t } = useTranslation()
  const { projectId, systemId } = useParams<{ projectId: string; systemId: string }>()
  const currentSystemIdRef = useRef(systemId)
  currentSystemIdRef.current = systemId
  // Where the reader came from. Arriving from the system library means
  // they were looking at ONE system across several vessels and clicked
  // into one of them — so the trail back is to that library row, not to
  // this vessel's project page, and the vessel is worth naming because
  // it is the thing that changed.
  const location = useLocation()
  const from = (location.state ?? {}) as { from?: string; systemName?: string; hull?: string }
  const cameFromLibrary = from.from === 'library'
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { success, error: showError, showToast } = useToast()

  // tab state lives in the URL so refresh / share-link preserves it.
  // default = 'risk-assessment' (no query param). other values map 1:1 to
  // the ?tab= URL value
  type SystemTab = 'risk-assessment' | 'legacy-ra' | 'asset-inventory' | 'word-ra' | 'evidence'
  const tabParam = searchParams.get('tab')
  const activeTab: SystemTab =
    tabParam === 'legacy-ra'       ? 'legacy-ra' :
    tabParam === 'asset-inventory' ? 'asset-inventory' :
    tabParam === 'word-ra'         ? 'word-ra' :
    tabParam === 'evidence'        ? 'evidence' :
                                     'risk-assessment'
  const setActiveTab = (tab: SystemTab) => {
    const next = new URLSearchParams(searchParams)
    if (tab === 'risk-assessment') next.delete('tab')
    else next.set('tab', tab)
    setSearchParams(next, { replace: true })
  }

  const [data, setData] = useState<AssessmentResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [importOpen, setImportOpen] = useState(false)
  const [droppedAssessmentFile, setDroppedAssessmentFile] = useState<File | null>(null)
  const [legacyImportOpen, setLegacyImportOpen] = useState(false)
  const [legacyParseResult, setLegacyParseResult] = useState<ParseAssessmentResultLegacy | null>(null)
  // bumped after a legacy import or delete to force LegacyRaView to remount
  // and re-fetch. simpler than threading a refresh callback into it
  const [legacyRefreshKey, setLegacyRefreshKey] = useState(0)
  const [fullEditOpen, setFullEditOpen] = useState(false)
  const [inventoryItems, setInventoryItems] = useState<AssetInventoryRow[]>([])
  const [vaiImportOpen, setVaiImportOpen] = useState(false)
  const [droppedVaiFile, setDroppedVaiFile] = useState<File | null>(null)
  // bumped after the page-level drop router uploads evidence externally
  // (e.g. PDF dropped while the Mappings tab is active). the Evidence
  // section reads this to refetch its list even if it's not mounted at
  // upload time
  const [evidenceUploadSignal, setEvidenceUploadSignal] = useState(0)
  const evidenceSectionRef = useRef<EvidenceSectionHandle | null>(null)
  const { user } = useAuth()
  // UX-only gates. server enforces requireAdmin/requireSuperAdmin. this
  // just hides affordances users would get 403'd on
  const canUpload = canUploadFiles(user)
  const canDeleteEvidence = canManage(user)
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
  const loadSequence = useRef(0)

  const load = useCallback(async () => {
    if (!systemId) {
      loadSequence.current++
      setData(null)
      setInventoryItems([])
      setLoading(false)
      return
    }
    if (currentSystemIdRef.current !== systemId) return
    const sequence = ++loadSequence.current
    setLoading(true)
    try {
      const [assessment, inventory] = await Promise.all([
        projectsApi.getAssessment(systemId),
        projectsApi.getSystemInventory(systemId).catch(() => [] as AssetInventoryRow[]),
      ])
      if (sequence !== loadSequence.current || currentSystemIdRef.current !== systemId) return
      // Commit both route-bound datasets together so an assessment can
      // never render beside inventory from a different system.
      setData(assessment)
      setInventoryItems(inventory)
    } catch (err: any) {
      if (sequence !== loadSequence.current || currentSystemIdRef.current !== systemId) return
      showError(err.message ?? 'Failed to load system assessment')
    } finally {
      if (sequence === loadSequence.current && currentSystemIdRef.current === systemId) setLoading(false)
    }
  }, [systemId, showError])

  useEffect(() => {
    // Clear the previous route immediately; request sequencing below keeps
    // late responses from repopulating it after a fast system switch.
    loadSequence.current++
    setData(null)
    setInventoryItems([])
    setImportOpen(false)
    setDroppedAssessmentFile(null)
    setVaiImportOpen(false)
    setDroppedVaiFile(null)
    setLegacyImportOpen(false)
    setLegacyParseResult(null)
    void load()
  }, [systemId, load])

  // scoped updates for the evidence array so upload/delete don't trigger a
  // full re-fetch via load(). load() flips `loading=true` and replaces
  // `data`, which remounts the whole page and loses scroll position
  const appendEvidence = useCallback((rows: EvidenceFile[]) => {
    if (rows.length === 0) return
    setData(prev => prev && prev.project_system.id === systemId
      ? { ...prev, evidence: [...prev.evidence, ...rows] }
      : prev)
  }, [systemId])

  const removeEvidence = useCallback((id: string) => {
    setData(prev => prev && prev.project_system.id === systemId
      ? { ...prev, evidence: prev.evidence.filter(e => e.id !== id) }
      : prev)
  }, [systemId])

  const onImportSuccess = async () => {
    setImportOpen(false)
    setDroppedAssessmentFile(null)
    success('Risk assessment imported')
    await load()
  }

  const handleFileDrop = (file: File) => {
    if (!systemId) return
    // Dropping only starts parsing. The existing modal owns the preview and
    // explicit Save action; no destructive commit happens from the drop.
    setDroppedAssessmentFile(file)
    setImportOpen(true)
  }

  const handleVaiFileDrop = (file: File) => {
    if (!systemId) return
    // As above, parse in the modal and require an explicit Import click.
    setDroppedVaiFile(file)
    setVaiImportOpen(true)
  }

  // "Clear this system's inventory" — the undo for a wrong per-system VAI
  // upload. the confirm names exactly what's going (count + system) before
  // anything is deleted; Word-RA-sourced rows are excluded server-side
  const handleClearInventory = async () => {
    if (!systemId || !data) return
    const name = data.project_system.system_name || data.project_system.system_description || 'this system'
    const clearable = inventoryItems.filter(
      r => r.source !== 'word_ra_inventory' && r.source !== 'ra_picker',
    ).length
    if (clearable === 0) {
      showError('No deletable inventory rows — all rows for this system come from Word RA mappings.')
      return
    }
    if (!window.confirm(
      `Delete ${clearable} inventory row${clearable !== 1 ? 's' : ''} for "${name}"? ` +
      'Hand-edited values in these rows will be lost. Word-RA-sourced rows are not affected.',
    )) return
    try {
      const r = await projectsApi.clearSystemAssetInventory(systemId)
      success(`Deleted ${r.deleted} inventory row${r.deleted !== 1 ? 's' : ''}`)
      await load()
    } catch (err: any) {
      showError(err.message ?? 'Failed to clear inventory')
    }
  }

  // ── Page-level drop router — HOOKS MUST STAY ABOVE THE EARLY RETURN ──
  //
  // HOTFIX 2026-06-12 (React #310, every System Detail page crashed):
  // e60a380 originally placed this useCallback + useDocumentDropRouter
  // (which carries useState/useRef/useEffect internally) BELOW the
  // `if (loading || !data) return` guard. first render (loading) committed
  // N hooks. when data arrived the full branch committed N+k and React
  // threw "rendered more hooks than the previous render". every hook now
  // sits unconditionally before any early return. the data-dependence
  // moved INSIDE the callbacks (defensive derivation + the `enabled` flag),
  // never around the hooks.
  //
  // unified page-level drop router (replaces the old root-div onDrop +
  // EvidenceSection's component-scoped zone, both fired for the same drop
  // pre-fix, race condition on .xlsx). document-level listener attached via
  // useEffect inside the hook with explicit cleanup, so the leaked-listener
  // regression from 6342a87 is impossible.
  //
  // routing:
  //   .xlsx / .xls → existing parse / VAI flow (route by active tab)
  //   PDF/DOCX/DOC/PNG/JPG → upload as evidence (any tab)
  //   anything else → reject toast
  //
  // dropSystemName derives from nullable `data`. during the loading render
  // it falls back to 'this system'. the router's classifyRef always reads
  // the latest closure so the name self-heals the moment data lands.
  const dropSystemName = data?.project_system
    ? (data.project_system.system_name || data.project_system.system_description || 'Unnamed system')
    : 'this system'

  const dropDecision = useCallback((f: { name: string; type: string }): DropDecision => {
    const isXls = /\.xlsx?$/i.test(f.name)
                || f.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                || f.type === 'application/vnd.ms-excel'
    if (isXls) {
      if (activeTab === 'word-ra') {
        return {
          action: 'reject',
          overlayMessage: 'Word RA uploads happen at the project level',
          rejectReason:   'open the project Overview tab to upload Word RA',
        }
      }
      const overlay = activeTab === 'asset-inventory'
        ? `Drop to import asset inventory into "${dropSystemName}"`
        : `Drop to parse risk assessment for "${dropSystemName}"`
      return { action: 'parse', overlayMessage: overlay }
    }
    // evidence types: pdf/docx/doc/png/jpg/jpeg. xlsx is deliberately
    // EXCLUDED here (parse wins for Excel) even though it's in
    // EVIDENCE_ALLOWED_EXTENSIONS for the upload-button surface.
    if (isEvidenceTypeAllowed(f.name) && !isXls) {
      return {
        action: 'evidence',
        overlayMessage: `Drop to attach as evidence to "${dropSystemName}"`,
      }
    }
    // empty name during dragover (DataTransfer.items don't expose
    // filenames), fall through with a generic accept-everything overlay.
    // the per-file classifier will reject on drop.
    if (!f.name) {
      return {
        action: 'evidence',
        overlayMessage: `Drop to attach as evidence to "${dropSystemName}"`,
      }
    }
    return {
      action: 'reject',
      overlayMessage: `Drop to attach evidence to "${dropSystemName}"`,
      rejectReason:   `unsupported type. Accepted: ${EVIDENCE_ALLOWED_LABEL}, or .xlsx for parsing.`,
    }
  }, [activeTab, dropSystemName])

  const { dragging, overlayMessage } = useDocumentDropRouter({
    classify: dropDecision,
    onParse: async (files) => {
      if (!canUpload) { showError('Assessment import requires admin role'); return }
      const file = files[0]
      if (!file) return
      if (activeTab === 'asset-inventory') await handleVaiFileDrop(file)
      else                                  await handleFileDrop(file)
    },
    onEvidence: async (files) => {
      if (!canUpload) { showError('Evidence upload requires admin role'); return }
      // mount the Evidence section if it isn't already (tab switch happens
      // AFTER the upload). try the imperative handle first, fall back to
      // switching tabs which triggers a refetch via the externalUploadSignal
      // bump.
      if (evidenceSectionRef.current) {
        await evidenceSectionRef.current.upload(files)
      } else {
        // section not mounted (user is on a different tab). upload through
        // the API directly. the section refetches when the user navigates
        // to it via the toast link.
        const { projectsApi } = await import('@/lib/projectsApi')
        let ok = 0
        for (const file of files) {
          try {
            await projectsApi.uploadSystemEvidence(systemId!, file)
            ok++
          } catch (err: any) {
            showError(`Upload failed for ${file.name}: ${err?.message ?? 'unknown'}`)
          }
        }
        if (ok > 0) {
          success(`${ok} file${ok === 1 ? '' : 's'} uploaded to evidence`)
        }
      }
      // tell the section to refetch on its next mount / next tick.
      setEvidenceUploadSignal(s => s + 1)
      // if the user is NOT on the Evidence tab, surface a link toast so
      // they know where the file went.
      if (activeTab !== 'evidence') {
        showToast({
          type: 'info',
          title: 'Evidence uploaded',
          message: `Open the Evidence tab to preview.`,
          action: { label: 'Go to Evidence', onClick: () => setActiveTab('evidence') },
          duration: 6000,
        })
      }
    },
    onReject: (msg) => showError(msg),
    // router stays disabled until data lands. drops during the loading
    // skeleton have no system context to route against.
    enabled: !!canUpload && !loading && !!data,
  })

  // ── Early return AFTER every hook above, never between hooks ──
  if (loading || !data || data.project_system.id !== systemId) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-sm text-slate-400">{t('common.loading')}</div>
      </div>
    )
  }

  const ps = data.project_system
  const cbs = data.cbs
  const profile = data.profile
  const isCbs = cbs?.is_cbs ?? null
  const systemDisplayName = ps.system_name || ps.system_description || 'Unnamed system'

  // count of answered CBS questions
  const answeredCount = cbs
    ? CBS_QUESTIONS.filter(q => cbs[`${q.code}_answer` as keyof typeof cbs] !== null).length
    : 0

  return (
    <DirtyFormProvider context="system_detail" onSaved={() => load()}>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white relative">
      {dragging && (
        <div className="fixed inset-0 z-40 bg-blue-500/10 dark:bg-blue-400/10 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl px-10 py-8 text-center border-2 border-dashed border-blue-400">
            <Upload className="h-8 w-8 text-blue-500 mx-auto mb-3" />
            <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {overlayMessage || 'Drop a file'}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {activeTab === 'word-ra'
                ? 'Open the project Overview tab and use the Manage Word RA drawer to upload.'
                : activeTab === 'asset-inventory'
                ? '.xlsx with sheet 3 (Vessel Asset Inventory). Preview before importing.'
                : '.xlsx with CBS assessment and vendor response sheets. Preview before saving.'}
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto px-4 py-4 max-w-[1200px]">

        <DirtyChangesBar />

        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 flex-shrink-0"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-slate-400 flex items-center gap-1">
              <span className="hover:underline cursor-pointer text-slate-500" onClick={() => navigate('/dashboard')}>
                Dashboard
              </span>
              <span>&rsaquo;</span>
              {cameFromLibrary && (
                <>
                  <span
                    className="hover:underline cursor-pointer text-slate-500"
                    onClick={() => navigate('/system-library')}
                    title={from.systemName ? `Back to ${from.systemName} in the library` : 'Back to the system library'}
                  >
                    System library
                  </span>
                  <span>&rsaquo;</span>
                </>
              )}
              <span className="hover:underline cursor-pointer text-slate-500" onClick={() => navigate(`/projects/${projectId}`)}>
                {from.hull ?? 'Project'}
              </span>
              <span>&rsaquo;</span>
              <span>{t('dashboardPage.riskAssessment')}</span>
            </div>
            <h1 className="text-lg font-semibold mt-0.5 leading-tight">{systemDisplayName}</h1>
            {ps.function_name && (
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{ps.function_name}</div>
            )}
            <div className="flex items-center gap-1.5 mt-2">
              {ps.vendor_name_raw && (
                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                  <Building2 className="h-2.5 w-2.5" />
                  {ps.vendor_name_raw}
                </span>
              )}
              {profile?.model_name && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                  {profile.model_name}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {canUpload && profile && (
              <button
                onClick={() => setFullEditOpen(true)}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                <Edit3 className="h-3.5 w-3.5" />
                Edit assessment
              </button>
            )}
            {canUpload && <div className="flex flex-col items-end gap-1">
              <button
                onClick={() => setImportOpen(true)}
                className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-3.5 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 hover:opacity-90"
              >
                <Upload className="h-3.5 w-3.5" />
                {cbs ? t('systemDetail.reimportRa') : t('systemDetail.importRa')}
              </button>
              <span className="text-[9px] text-slate-400">{t('systemDetail.dragXlsx')}</span>
            </div>}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-slate-200 dark:border-slate-700 mb-3.5">
          <div className="flex gap-6 -mb-px">
            {([
              { key: 'risk-assessment',  label: t('dashboardPage.riskAssessment') },
              { key: 'legacy-ra',        label: 'Legacy RA' },
              { key: 'asset-inventory',  label: t('nav.assetInventory') },
              { key: 'word-ra',          label: 'Word RA' },
              { key: 'evidence',         label: t('wordRa.evidence') },
            ] as const).map(t => {
              const active = activeTab === t.key
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={
                    'pb-2.5 pt-1 text-sm font-medium border-b-2 transition-colors ' +
                    (active
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200')
                  }
                >
                  {t.label}
                  {t.key === 'asset-inventory' && inventoryItems.length > 0 && (
                    <span className="ml-1.5 text-xs font-normal text-slate-400">
                      ({inventoryItems.length})
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {activeTab === 'risk-assessment' && (<>

        {/* Status */}
        <div className={`rounded-lg p-4 mb-3.5 flex items-center gap-4 ${
          isCbs === null
            ? 'bg-slate-100 dark:bg-slate-800/60'
            : isCbs
              ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40'
              : 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40'
        }`}>
          <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
            isCbs === null
              ? 'bg-white dark:bg-slate-700'
              : isCbs
                ? 'bg-amber-100 dark:bg-amber-900/40'
                : 'bg-emerald-100 dark:bg-emerald-900/40'
          }`}>
            {isCbs === null ? (
              <Shield className="h-5 w-5 text-slate-400" />
            ) : isCbs ? (
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            ) : (
              <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-sm font-medium ${
              isCbs === null ? '' : isCbs ? 'text-amber-900 dark:text-amber-200' : 'text-emerald-900 dark:text-emerald-200'
            }`}>
              {isCbs === null ? 'Ready for risk assessment' : isCbs ? 'Classified as CBS' : 'Not a CBS'}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
              {isCbs === null && 'Import the vendor\'s completed risk assessment workbook (.xlsx) to populate the CBS checklist and vendor response form.'}
              {isCbs === true && 'Computer-Based System — subject to cybersecurity risk assessment. Supporting evidence required.'}
              {isCbs === false && 'All 6 questions answered No — no further vendor response needed.'}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-lg font-semibold text-slate-900 dark:text-white">{answeredCount} / 6</div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400">{t('systemDetail.questionsAnswered')}</div>
            {cbs?.imported_at && (
              <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1 justify-end">
                <Calendar className="h-2.5 w-2.5" />
                {new Date(cbs.imported_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>

        {/* Profile reuse banner */}
        {profile && data.linked_project_systems.length > 1 && (
          <div className="mb-3.5 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start gap-2">
              <Ship className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 text-[11px]">
                <span className="text-blue-900 dark:text-blue-200 font-medium">{t('systemDetail.sharedProfile')}</span>
                <span className="text-blue-700 dark:text-blue-300"> — used on {data.linked_project_systems.length} vessels: </span>
                {data.linked_project_systems.map((link, i) => (
                  <React.Fragment key={link.id}>
                    {i > 0 && <span className="text-blue-400">, </span>}
                    <button
                      onClick={() => navigate(`/projects/${link.project_id}/systems/${link.id}`)}
                      className={`underline hover:no-underline ${link.id === ps.id ? 'font-semibold' : ''}`}
                    >
                      {link.shipyard_name ?? '?'} / {link.hull_number}
                    </button>
                  </React.Fragment>
                ))}
                <div className="text-[9px] text-blue-600 dark:text-blue-400 mt-0.5">
                  Updates apply to all linked vessels.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CBS + Vendor Responses */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-3.5">

          {/* CBS Initial Assessment */}
          <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="px-3.5 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <div className="text-[13px] font-medium">{t('systemDetail.cbsInitial')}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{t('systemDetail.cbsHint')}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                isCbs === null
                  ? 'bg-slate-100 dark:bg-slate-700 text-slate-500'
                  : isCbs
                    ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                    : 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
              }`}>
                {isCbs === null ? 'Not assessed' : isCbs ? 'CBS' : 'Non-CBS'}
              </span>
            </div>
            <div>
              {CBS_QUESTIONS.map(q => {
                const answer = cbs ? (cbs[`${q.code}_answer` as keyof typeof cbs] as string | null) : null
                const note = cbs ? (cbs[`${q.code}_evidence_note` as keyof typeof cbs] as string | null) : null
                const qEvidence = data.evidence.filter(e => e.attached_to_type === 'cbs_question' && e.attached_to_key === q.code)

                return (
                  <div key={q.code} className="px-3.5 py-2.5 border-t border-slate-100 dark:border-slate-700/60 first:border-t-0">
                    <div className="flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-[10px] font-semibold text-slate-500 dark:text-slate-400 flex-shrink-0">
                        {q.num}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400">{t(`systemDetail.questionCategories.${q.category}`, { defaultValue: q.category })}</div>
                        <div className="text-[12px] text-slate-700 dark:text-slate-200 leading-snug mt-0.5">{t(`systemDetail.questions.${q.code}`, { defaultValue: q.text })}</div>
                      </div>
                      <div className="flex-shrink-0">
                        <EditablePill
                          value={answer as 'Y' | 'N' | null}
                          disabled={!canUpload || !cbs}
                          dirtyKey={cbs ? { table: 'cbs_assessments', id: cbs.id, field: `${q.code}_answer` } : undefined}
                          onChange={async (v) => {
                            if (!cbs) return
                            try {
                              await projectsApi.updateCbs(cbs.id, { [`${q.code}_answer`]: v } as any)
                              await load()
                            } catch (err: any) {
                              showError(err.message ?? 'Failed to update')
                            }
                          }}
                        />
                      </div>
                    </div>
                    {cbs && (
                      <div className="ml-7 mt-1 text-[10px] text-slate-500 dark:text-slate-400 italic">
                        <EditableText
                          value={note}
                          disabled={!canUpload}
                          emptyText="add evidence note"
                          dirtyKey={{ table: 'cbs_assessments', id: cbs.id, field: `${q.code}_evidence_note` }}
                          onSave={async (v) => {
                            await projectsApi.updateCbs(cbs.id, { [`${q.code}_evidence_note`]: v } as any)
                            await load()
                          }}
                        />
                      </div>
                    )}
                    {(qEvidence.length > 0 || answer === 'Y') && profile && (
                      <div className="ml-7 mt-1.5 flex flex-wrap gap-1">
                        {qEvidence.map(ev => (
                          <button
                            key={ev.id}
                            type="button"
                            onClick={async () => {
                              try {
                                const url = await projectsApi.signEvidenceDownload(ev.id)
                                window.location.href = url
                              } catch {
                                showError('Could not prepare download')
                              }
                            }}
                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400"
                          >
                            <FileText className="h-2.5 w-2.5" />
                            <span className="max-w-[160px] truncate">{ev.original_filename}</span>
                          </button>
                        ))}
                        {canUpload && answer === 'Y' && (
                          <EvidenceUploader
                            profileId={profile.id}
                            attachedToType="cbs_question"
                            attachedToKey={q.code}
                            onUploaded={appendEvidence}
                            compact
                          />
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Vendor Response Form */}
          <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="px-3.5 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div>
                <div className="text-[13px] font-medium">{t('systemDetail.vendorResponse')}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Sheet 02 — only required for CBS systems
                </div>
              </div>
              {isCbs === null ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 flex items-center gap-1">
                  <Lock className="h-2.5 w-2.5" /> Locked
                </span>
              ) : isCbs ? (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                  {data.vendor_responses.length} responses
                </span>
              ) : (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 font-medium">
                  Optional
                </span>
              )}
            </div>

            {isCbs === null ? (
              <div className="py-10 px-5 text-center">
                <Lock className="h-6 w-6 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                <div className="text-[12px] text-slate-500 dark:text-slate-400">{t('systemDetail.waitingClassification')}</div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 max-w-sm mx-auto">
                  If any question on the left is answered Yes, vendor responses (A1–E2) become required and will appear here.
                </div>
              </div>
            ) : data.vendor_responses.length === 0 ? (
              <div className="py-10 px-5 text-center">
                <FileText className="h-6 w-6 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                <div className="text-[12px] text-slate-500 dark:text-slate-400">{t('systemDetail.noResponses')}</div>
                <div className="text-[10px] text-slate-400 dark:text-slate-500 mt-1">
                  {isCbs
                    ? 'Re-import the workbook to populate vendor responses.'
                    : 'Optionally re-import the workbook to populate vendor responses.'}
                </div>
              </div>
            ) : (
              <div>
                {data.vendor_responses.map(v => (
                  <div key={v.id} className="px-3.5 py-2.5 border-t border-slate-100 dark:border-slate-700/60 first:border-t-0">
                    <div className="flex items-start gap-2.5">
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 font-semibold flex-shrink-0">
                        {v.question_code}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400">{v.category}</div>
                        <div className="text-[12px] text-slate-700 dark:text-slate-200 leading-snug mt-0.5">{v.question_text}</div>
                        <div className="mt-1 text-[11px]">
                          <span className="text-slate-400">&rarr; </span>
                          <span className="font-medium text-slate-700 dark:text-slate-200">
                            <EditableText
                              value={v.dropdown_response}
                              disabled={!canUpload}
                              emptyText="add response"
                              dirtyKey={{ table: 'vendor_responses', id: v.id, field: 'dropdown_response' }}
                              onSave={async (val) => {
                                await projectsApi.updateVendorResponse(v.id, { dropdown_response: val })
                                await load()
                              }}
                            />
                          </span>
                        </div>
                        <div className="mt-1.5 text-[11px] text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900/40 px-2 py-1.5 rounded leading-relaxed">
                          <EditableText
                            value={v.detailed_response}
                            disabled={!canUpload}
                            multiline
                            emptyText="add detailed description"
                            dirtyKey={{ table: 'vendor_responses', id: v.id, field: 'detailed_response' }}
                            onSave={async (val) => {
                              await projectsApi.updateVendorResponse(v.id, { detailed_response: val })
                              await load()
                            }}
                          />
                        </div>
                        {/* remark: optional supplementary info, visually subordinate */}
                        <div className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400 italic flex items-baseline gap-1.5">
                          <span className="text-[9px] uppercase tracking-wider text-slate-400 not-italic flex-shrink-0">
                            Remark
                          </span>
                          <span className="flex-1 min-w-0">
                            <EditableText
                              value={v.remark}
                              disabled={!canUpload}
                              multiline
                              emptyText="add remark"
                              dirtyKey={{ table: 'vendor_responses', id: v.id, field: 'remark' }}
                              onSave={async (val) => {
                                await projectsApi.updateVendorResponse(v.id, { remark: val })
                                await load()
                              }}
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Supporting Evidence */}
        {profile && (
          <div className="mt-3.5 bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="px-3.5 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3">
              <div>
                <div className="text-[13px] font-medium">{t('systemDetail.supportingEvidence')}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{t('systemDetail.evidenceTypes')}</div>
              </div>
              {isCbs === false && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 font-medium flex-shrink-0">
                  Optional
                </span>
              )}
            </div>
            <div className="p-3.5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              {ATTACHMENT_SLOTS.map(f => {
                const evidence = data.evidence.filter(e => e.attached_to_type === 'vendor_response' && e.attached_to_key === f.key)
                return (
                  <div
                    key={f.key}
                    tabIndex={canUpload ? 0 : undefined}
                    role={canUpload ? 'button' : undefined}
                    aria-label={t('systemDetail.pasteInto', { label: t(`systemDetail.attachments.${f.key}`, { defaultValue: f.label }) })}
                    className={`border border-slate-200 dark:border-slate-700 rounded-lg p-2.5 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:border-blue-400 ${canUpload ? 'cursor-pointer' : ''}`}
                    onDragEnter={e => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add('border-blue-400', 'bg-blue-50', 'dark:bg-blue-900/20') }}
                    onDragOver={e => { e.preventDefault(); e.stopPropagation() }}
                    onDragLeave={e => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50', 'dark:bg-blue-900/20') }}
                    onDrop={async e => {
                      e.preventDefault(); e.stopPropagation()
                      e.currentTarget.classList.remove('border-blue-400', 'bg-blue-50', 'dark:bg-blue-900/20')
                      if (!canUpload) {
                        showError('Upload requires admin role')
                        return
                      }
                      if (!profile || !e.dataTransfer.files?.length) return
                      const created: EvidenceFile[] = []
                      for (const file of Array.from(e.dataTransfer.files)) {
                        const sizeError = validateUploadSize(file)
                        if (sizeError) {
                          showError(sizeError)
                          continue
                        }
                        try {
                          const row = await projectsApi.uploadEvidence(profile.id, file, {
                            attached_to_type: 'vendor_response',
                            attached_to_key: f.key,
                          })
                          created.push(row)
                        } catch (err: any) {
                          showError(err?.message ?? `Upload failed for ${file.name}`)
                        }
                      }
                      appendEvidence(created)
                    }}
                    onPaste={async e => {
                      if (!profile) return
                      if (!canUpload) {
                        showError('Upload requires admin role')
                        return
                      }
                      const items = Array.from(e.clipboardData?.items ?? [])
                      const imageItems = items.filter(it => it.kind === 'file' && it.type.startsWith('image/'))
                      if (imageItems.length === 0) return

                      e.preventDefault()
                      e.stopPropagation()

                      const ts = new Date().toISOString().replace(/[:.]/g, '-')
                      const created: EvidenceFile[] = []
                      let i = 0
                      for (const item of imageItems) {
                        const blob = item.getAsFile()
                        if (!blob) continue
                        const ext = (() => {
                          const m = blob.type.match(/^image\/(\w+)$/)
                          const sub = m?.[1]?.toLowerCase()
                          if (sub === 'jpeg') return 'jpg'
                          if (sub && /^[a-z0-9]+$/.test(sub)) return sub
                          return 'bin'
                        })()
                        const suffix = imageItems.length > 1 ? `_${i + 1}` : ''
                        const name = `${sanitizeFilename(systemDisplayName)}_${f.key}_${ts}${suffix}.${ext}`
                        const file = new File([blob], name, { type: blob.type })
                        const sizeError = validateUploadSize(file)
                        if (sizeError) {
                          showError(sizeError)
                          i++
                          continue
                        }
                        try {
                          const row = await projectsApi.uploadEvidence(profile.id, file, {
                            attached_to_type: 'vendor_response',
                            attached_to_key: f.key,
                          })
                          created.push(row)
                        } catch (err: any) {
                          showError(err?.message ?? `Upload failed for ${name}`)
                        }
                        i++
                      }
                      appendEvidence(created)
                    }}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className="text-[10px] font-mono text-slate-400">{f.key}</span>
                      <span className="text-[11px] font-medium flex-1">{t(`systemDetail.attachments.${f.key}`, { defaultValue: f.label })}</span>
                      {f.required && isCbs && <span className="text-[9px] text-red-500 font-bold">*</span>}
                    </div>
                    {evidence.length > 0 && (
                      <div className="space-y-1 mb-1.5">
                        {evidence.map(ev => (
                          <div key={ev.id} className="flex items-center gap-1.5 text-[10px] bg-slate-50 dark:bg-slate-900/40 px-1.5 py-1 rounded group">
                            <FileText className="h-2.5 w-2.5 text-slate-400 flex-shrink-0" />
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const url = await projectsApi.signEvidenceDownload(ev.id)
                                  window.location.href = url
                                } catch {
                                  showError('Could not prepare download')
                                }
                              }}
                              className="flex-1 truncate text-left text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {ev.original_filename}
                            </button>
                            {canDeleteEvidence && (
                              <button
                                onClick={async () => {
                                  if (!confirm('Delete this file?')) return
                                  try {
                                    await projectsApi.deleteEvidence(ev.id)
                                    removeEvidence(ev.id)
                                  } catch (err: any) {
                                    showError(err?.message ?? 'Delete failed')
                                  }
                                }}
                                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
                              >
                                <Trash2 className="h-2.5 w-2.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {canUpload && (
                      <>
                        <div className="text-[9px] text-slate-400 mb-1">
                          Click box, then {isMac ? '⌘' : 'Ctrl'}+V to paste image · Max {MAX_UPLOAD_LABEL} per file
                        </div>
                        {/* #4: clipboard delivers a single image per paste (OS-level).
                            the handler loops but only ever receives one. steer users to
                            drag-and-drop / file-select for multiple */}
                        <div className="text-[9px] text-slate-400 mb-1">
                          한 번에 한 장씩 붙여넣기됩니다. 여러 장은 드래그앤드롭 또는 파일 선택을 사용하세요.
                        </div>
                      </>
                    )}
                    {canUpload && (
                      <EvidenceUploader
                        profileId={profile.id}
                        attachedToType="vendor_response"
                        attachedToKey={f.key}
                        onUploaded={appendEvidence}
                        compact
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        </>)}

        {activeTab === 'legacy-ra' && systemId && (
          <LegacyRaView
            key={legacyRefreshKey}
            projectSystemId={systemId}
            canDelete={canManage(user)}
            onDeleted={() => setLegacyRefreshKey(k => k + 1)}
          />
        )}

        {activeTab === 'word-ra' && systemId && (
          <WordRaSystemView projectSystemId={systemId} />
        )}

        {activeTab === 'evidence' && systemId && (
          <EvidenceSection
            ref={evidenceSectionRef}
            projectSystemId={systemId}
            externalUploadSignal={evidenceUploadSignal}
          />
        )}

        {activeTab === 'asset-inventory' && (
          inventoryItems.length === 0 ? (
            <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-12 text-center">
              <Package className="h-10 w-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <div className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                No inventory items linked to this system yet
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-4">
                {t('systemDetail.inventoryHint')}
              </div>
              <div className="flex items-center justify-center gap-2">
                {canUpload && (
                  <button
                    onClick={() => setVaiImportOpen(true)}
                    className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5"
                  >
                    <Upload className="h-3 w-3" />
                    {t('assetInventory.import')}
                  </button>
                )}
                <button
                  onClick={() => navigate(`/projects/${projectId}?tab=inventory`)}
                  className="text-xs px-3 py-1.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600"
                >
                  View project inventory →
                </button>
              </div>
            </div>
          ) : (
            // action bar above the shared component: Import + project-link
            // (the AssetInventoryTab's own header has the title + search +
            // Full/Compact toggle. we render these adjacent rather than
            // squeezing them into its header to keep the component reusable
            // across both pages without per-caller header customisation).
            <>
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="text-[10px] text-slate-500 dark:text-slate-400">
                  IACS Rec 190 — items linked to this system
                </div>
                <div className="flex items-center gap-2">
                  {canUpload && (
                    <button
                      onClick={() => setVaiImportOpen(true)}
                      className="text-[10px] px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1"
                    >
                      <Upload className="h-3 w-3" />
                      Import
                    </button>
                  )}
                  {canUpload && (
                    <button
                      onClick={handleClearInventory}
                      title={t('systemDetail.deleteInventoryTitle')}
                      className="text-[10px] px-2 py-1 rounded border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/projects/${projectId}?tab=inventory`)}
                    className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    View all in project inventory →
                  </button>
                </div>
              </div>
              <AssetInventoryTab
                rows={inventoryItems}
                canEdit={false}
                onChanged={load}
                defaultViewMode="compact"
              />
            </>
          )
        )}
      </div>

      {/* Full edit modal */}
      {canUpload && fullEditOpen && profile && data.cbs && (
        <FullEditAssessmentModal
          profile={profile}
          cbs={data.cbs}
          vendorResponses={data.vendor_responses}
          onClose={() => setFullEditOpen(false)}
          onSaved={() => { setFullEditOpen(false); load() }}
        />
      )}

      {/* Import modal */}
      {canUpload && importOpen && systemId && (
        <ImportAssessmentModal
          key={`${systemId}:${droppedAssessmentFile?.name ?? 'picker'}`}
          projectSystemId={systemId}
          initialFile={droppedAssessmentFile}
          onClose={() => {
            setImportOpen(false)
            setDroppedAssessmentFile(null)
          }}
          onSuccess={onImportSuccess}
          onLegacyDetected={(result) => {
            setLegacyParseResult(result)
            setLegacyImportOpen(true)
          }}
        />
      )}

      {/* System-scoped VAI import modal */}
      {canUpload && vaiImportOpen && systemId && (
        <ImportSystemAssetInventoryModal
          key={`${systemId}:${droppedVaiFile?.name ?? 'picker'}`}
          projectSystemId={systemId}
          initialFile={droppedVaiFile}
          onClose={() => {
            setVaiImportOpen(false)
            setDroppedVaiFile(null)
          }}
          onSuccess={async () => {
            setVaiImportOpen(false)
            setDroppedVaiFile(null)
            await load()
          }}
        />
      )}

      {/* Legacy RA import modal */}
      {canUpload && legacyImportOpen && legacyParseResult && systemId && (
        <ImportLegacyRaModal
          projectSystemId={systemId}
          parseResult={legacyParseResult}
          onClose={() => setLegacyImportOpen(false)}
          onCommitted={() => {
            setLegacyImportOpen(false)
            setLegacyParseResult(null)
            setLegacyRefreshKey(k => k + 1)
            setActiveTab('legacy-ra')
          }}
        />
      )}
    </div>
    </DirtyFormProvider>
  )
}
